// Technical debt payback: a fix costs a principal (team-days) and wins back part of the
// interest (team-days lost per month). Savings run over a horizon, grow if the area is
// changed more often, and stop if the code is replaced. Every input is a range, so the
// payback time and the net benefit are distributions, and the expected value of perfect
// information says whether to measure the interest before deciding.
// Source: docs/backlog-prioritization-research.md#6-improve-internal-work-refactoring-efficiency.
// Tests: model.test.js.

import { Rng } from "../lib/rng.js";
import { cdfPoints, drawLognormal, drawUniform, lognormalFrom90, meanOf, quantilesOf, shareWhere } from "../lib/ranges.js";

export const DRAWS = 20000;
export const SEED = 20261016;

/**
 * Problems with the inputs, in words; empty when usable.
 * @param {object} s page state
 * @returns {string[]}
 */
export function inputIssues(s) {
  const out = [];
  const pos = (r, name) => {
    if (!(Number.isFinite(r.low) && Number.isFinite(r.high))) out.push(`${name} needs both ends.`);
    else if (!(r.low > 0)) out.push(`${name} must be above 0.`);
    else if (r.low > r.high) out.push(`${name} runs backwards.`);
  };
  pos(s.principal, "Cost of the fix");
  pos(s.interest, "Time lost each month");
  if (!(Number.isFinite(s.recovered.low) && Number.isFinite(s.recovered.high))) out.push("Share won back needs both ends.");
  else if (s.recovered.low < 0 || s.recovered.high > 100 || s.recovered.low > s.recovered.high) out.push("Share won back must run from low to high within 0–100%.");
  if (!(s.horizonMonths > 0)) out.push("The horizon must be above 0 months.");
  return out;
}

/**
 * Savings from month 0 to T at monthly rate S, with continuous growth g and hazard h
 * of the code being replaced: S·∫₀ᵀ e^{(g−h)t} dt.
 * @param {number} S team-days saved per month at the start
 * @param {number} rate g − h, per month
 * @param {number} T months
 * @returns {number}
 */
export function savingsOver(S, rate, T) {
  return Math.abs(rate) < 1e-12 ? S * T : (S * (Math.exp(rate * T) - 1)) / rate;
}

/**
 * Months until cumulative savings equal the principal; Infinity if they never do.
 * @param {number} P principal
 * @param {number} S monthly saving at the start
 * @param {number} rate g − h, per month
 * @returns {number}
 */
export function paybackMonths(P, S, rate) {
  if (!(S > 0)) return Infinity;
  if (Math.abs(rate) < 1e-12) return P / S;
  const arg = 1 + (P * rate) / S;
  return arg > 0 ? Math.log(arg) / rate : Infinity;
}

/** Monthly rates from yearly percentages: growth of the interest and chance the code is replaced. */
export function monthlyRate(growthPct, replacePct) {
  const g = Math.log(1 + growthPct / 100) / 12;
  const h = replacePct >= 100 ? Infinity : -Math.log(1 - replacePct / 100) / 12;
  return g - h;
}

/**
 * @param {object} s page state: principal, interest, recovered (ranges), horizonMonths, growthPct, replacePct
 * @param {{ draws?: number, seed?: number }} [opts]
 * @returns {object}
 */
export function analyseDebt(s, { draws = DRAWS, seed = SEED } = {}) {
  const issues = inputIssues(s);
  if (issues.length) return { issues };
  const P = s.principal.high > s.principal.low ? lognormalFrom90(s.principal.low, s.principal.high) : null;
  const I = s.interest.high > s.interest.low ? lognormalFrom90(s.interest.low, s.interest.high) : null;
  const rate = monthlyRate(s.growthPct, s.replacePct);
  const rng = new Rng(seed);
  const net = new Float64Array(draws);
  const saved = new Float64Array(draws);
  const payback = new Float64Array(draws);
  for (let d = 0; d < draws; d++) {
    const p = P ? drawLognormal(rng, P) : s.principal.low;
    const i = I ? drawLognormal(rng, I) : s.interest.low;
    const r = drawUniform(rng, s.recovered.low, s.recovered.high) / 100;
    const S = i * r;
    saved[d] = Number.isFinite(rate) ? savingsOver(S, rate, s.horizonMonths) : 0;
    net[d] = saved[d] - p;
    payback[d] = Number.isFinite(rate) ? paybackMonths(p, S, rate) : Infinity;
  }
  const meanNet = meanOf(net);
  const [n5, n50, n95] = quantilesOf(net, [0.05, 0.5, 0.95]);
  const [s5, s50, s95] = quantilesOf(saved, [0.05, 0.5, 0.95]);
  const [b5, b50, b95] = quantilesOf(payback, [0.05, 0.5, 0.95]);
  // Deciding now: fix if the expected net is above 0. Knowing every input first would let
  // you fix only when the net is above 0: EVPI = E[max(net, 0)] − max(E[net], 0).
  const evpi = meanOf(net.map((x) => Math.max(x, 0))) - Math.max(meanNet, 0);
  const finitePayback = payback.filter(Number.isFinite);
  return {
    issues,
    fix: meanNet > 0,
    meanNet,
    net: { p5: n5, p50: n50, p95: n95 },
    saved: { p5: s5, p50: s50, p95: s95 },
    chancePaysBack: shareWhere(net, (x) => x > 0),
    payback: { p5: b5, p50: b50, p95: b95 },
    chanceNever: shareWhere(payback, (x) => !Number.isFinite(x)),
    evpi,
    curve: finitePayback.length ? cdfPoints(finitePayback, 120).map(([x, p]) => [x, (p * finitePayback.length) / draws]) : [],
  };
}

/** Days, rounded for the page. */
export function fmt(x) {
  if (!Number.isFinite(x)) return "never";
  const a = Math.abs(x);
  return a >= 100 ? Math.round(x).toLocaleString("en-US") : (Math.round(x * 10) / 10).toString();
}
export const pct = (p) => (Number.isFinite(p) ? `${Math.round(p * 100)}%` : "—");

/**
 * Plain sentences for a ticket, a channel or an agent's prompt.
 * @param {object} r analyseDebt result
 * @param {object} s page state
 * @returns {string[]}
 */
export function summaryLines(r, s) {
  if (r.issues.length) return [];
  const label = s.label || "This fix";
  return [
    `${label}: ${pct(r.chancePaysBack)} chance it pays back within ${s.horizonMonths} months; expected net ${fmt(r.meanNet)} team-days (90% range ${fmt(r.net.p5)} to ${fmt(r.net.p95)}).`,
    `Payback most likely after ${fmt(r.payback.p50)} months (90% range ${fmt(r.payback.p5)} to ${fmt(r.payback.p95)}).`,
    r.evpi > 0.1 * Math.abs(r.meanNet) && r.evpi > 0.5
      ? `Measuring the time lost before deciding is worth up to ${fmt(r.evpi)} team-days: the decision depends on it.`
      : "Measuring more first would hardly change the decision.",
    "Conditional on the ranges given for cost, time lost and share won back, and the stated chance the code is replaced.",
  ];
}
